import { useEffect, useState, useCallback } from 'react';

export const PAGES = {
  DASHBOARD: 'dashboard',
  REPORT: 'report',
  TEAM: 'team',
  SETTINGS: 'settings',
  ADMIN: 'admin',
  ACCEPT_INVITE: 'accept-invite',
  LOGIN: 'login',
  SIGNUP: 'signup',
  VERIFY_EMAIL: 'verify',
  FORGOT_PASSWORD: 'forgot',
  RESET_PASSWORD: 'reset',
  PRIVACY: 'privacy',
  TERMS: 'terms',
};

export const PUBLIC_PAGES = new Set([
  PAGES.LOGIN,
  PAGES.SIGNUP,
  PAGES.VERIFY_EMAIL,
  PAGES.FORGOT_PASSWORD,
  PAGES.RESET_PASSWORD,
  PAGES.ACCEPT_INVITE,
  // Legal pages must render for logged-out visitors (the signup page links to
  // them before an account exists), so they are public.
  PAGES.PRIVACY,
  PAGES.TERMS,
]);

const REPORT_PATH_RE = /^\/reports\/([A-Z0-9-]+)\/?$/i;

export function parsePath(pathname, search) {
  const params = new URLSearchParams(search || '');

  if (pathname === '/login') return { page: PAGES.LOGIN };
  if (pathname === '/signup') return { page: PAGES.SIGNUP };
  if (pathname === '/privacy' || pathname === '/privacy/') return { page: PAGES.PRIVACY };
  if (pathname === '/terms' || pathname === '/terms/') return { page: PAGES.TERMS };
  if (pathname === '/verify-email') {
    return { page: PAGES.VERIFY_EMAIL, email: params.get('email') };
  }
  if (pathname === '/forgot-password') return { page: PAGES.FORGOT_PASSWORD };
  if (pathname === '/reset-password') {
    return { page: PAGES.RESET_PASSWORD, token: params.get('token') };
  }
  if (pathname === '/accept-invite') {
    return { page: PAGES.ACCEPT_INVITE, token: params.get('token') };
  }
  if (pathname === '/team' || pathname === '/team/') {
    return { page: PAGES.TEAM };
  }
  if (pathname === '/settings' || pathname === '/settings/') {
    return { page: PAGES.SETTINGS, section: params.get('section') || null };
  }
  if (pathname === '/admin' || pathname === '/admin/') {
    return { page: PAGES.ADMIN };
  }

  const m = pathname.match(REPORT_PATH_RE);
  if (m) return { page: PAGES.REPORT, reportId: m[1] };

  // Legacy `/?report=<id>` — preserve for old PDF links and existing tests.
  // Also treat a bare `/` as a legacy public view (empty-state) so the
  // pre-9B-1 App.render test ("shows a helpful empty state when no report
  // is in the URL") continues to pass without modification. Authenticated
  // users reach the new dashboard via `/dashboard`.
  if (pathname === '/' || pathname === '') {
    const legacyId = params.get('report');
    return { page: PAGES.REPORT, reportId: legacyId, isLegacy: true };
  }

  if (pathname === '/dashboard' || pathname === '/dashboard/') {
    return { page: PAGES.DASHBOARD };
  }

  // Unknown path — treat as dashboard (will require auth).
  return { page: PAGES.DASHBOARD };
}

export function usePageRouter() {
  const [route, setRoute] = useState(() =>
    typeof window === 'undefined'
      ? { page: PAGES.DASHBOARD }
      : parsePath(window.location.pathname, window.location.search),
  );

  useEffect(() => {
    const onPop = () =>
      setRoute(parsePath(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((pathname, search = '') => {
    const q = search ? (search.startsWith('?') ? search.slice(1) : search) : '';
    const full = q ? `${pathname}?${q}` : pathname;
    window.history.pushState({}, '', full);
    setRoute(parsePath(pathname, q));
  }, []);

  const replace = useCallback((pathname, search = '') => {
    const q = search ? (search.startsWith('?') ? search.slice(1) : search) : '';
    const full = q ? `${pathname}?${q}` : pathname;
    window.history.replaceState({}, '', full);
    setRoute(parsePath(pathname, q));
  }, []);

  return { route, navigate, replace };
}
